import React from 'react';
import AuthImage from '../AuthImage';
import {ChevronDownIcon, ChevronUpIcon, PlusIcon, SparklesIcon, TrashIcon} from '../icons';
import type {ImageSlot} from './types';

export interface ItemFormPhotosProps {
    images: ImageSlot[];
    onSelectFiles: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onMove: (index: number, dir: 'up' | 'down') => void;
    onRemove: (index: number) => void;
    /** Если передан — на первом экране появляется путь «Распознать по фото». */
    onRecognizeByPhoto?: () => void;
}

/**
 * Фотографии — первое, что видит пользователь: добавление вещи начинается с
 * снимка, а не со знакомства с моделью данных (QA-13). Рядом — явный
 * альтернативный путь «Распознать по фото» (AI заполняет поля сам).
 */
const ItemFormPhotos: React.FC<ItemFormPhotosProps> = ({
    images, onSelectFiles, onMove, onRemove, onRecognizeByPhoto
}) => (
    <>
        <p className="text-sm text-gray-500 dark:text-gray-400">
            Первое фото — главное, его видно в каталоге.
        </p>
        <div className="flex flex-wrap gap-3">
            {images.map((slot, idx) => (
                <div
                    key={slot.id}
                    className="relative group w-24 h-24 rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-600"
                >
                    {slot.error ? (
                        <div className="w-full h-full flex items-center justify-center bg-red-50 dark:bg-red-900/20 text-red-600 text-xs p-1 text-center">
                            Ошибка
                        </div>
                    ) : slot.key && !slot.file ? (
                        <AuthImage
                            imageKey={slot.key}
                            alt=""
                            variant="thumbnail"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <img src={slot.previewUrl} alt="" className="w-full h-full object-cover"/>
                    )}
                    {slot.uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <span className="text-white text-xs">Загрузка...</span>
                        </div>
                    )}
                    {idx === 0 && !slot.uploading && !slot.error && (
                        <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-primary-600 text-white text-xs rounded">
                            Главная
                        </div>
                    )}
                    <div className="absolute top-1 right-1 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-ui ease-out">
                        <button
                            type="button"
                            aria-label="Переместить фото выше"
                            onClick={() => onMove(idx, 'up')}
                            disabled={idx === 0}
                            className="p-1 bg-white/90 dark:bg-gray-800/90 rounded"
                        >
                            <ChevronUpIcon className="w-4 h-4"/>
                        </button>
                        <button
                            type="button"
                            aria-label="Переместить фото ниже"
                            onClick={() => onMove(idx, 'down')}
                            disabled={idx === images.length - 1}
                            className="p-1 bg-white/90 dark:bg-gray-800/90 rounded"
                        >
                            <ChevronDownIcon className="w-4 h-4"/>
                        </button>
                        <button
                            type="button"
                            aria-label="Удалить фото"
                            onClick={() => onRemove(idx)}
                            className="p-1 bg-red-100 dark:bg-red-900/50 rounded text-red-600"
                        >
                            <TrashIcon className="w-4 h-4"/>
                        </button>
                    </div>
                </div>
            ))}
            <label className="w-24 h-24 flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-primary-500 cursor-pointer transition-colors duration-ui ease-out">
                <PlusIcon className="w-7 h-7 text-gray-400"/>
                <span className="text-xs text-gray-500 dark:text-gray-400">Фото</span>
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="item-form-photo-input"
                    aria-label="Добавить фотографии"
                    onChange={onSelectFiles}
                />
            </label>
        </div>
        {onRecognizeByPhoto && (
            <button
                type="button"
                onClick={onRecognizeByPhoto}
                data-testid="item-form-recognize"
                className="btn-secondary w-full sm:w-auto"
            >
                <SparklesIcon className="w-5 h-5"/>
                Распознать по фото
            </button>
        )}
    </>
);

export default ItemFormPhotos;
